<template>
  <div v-if="!fetchingGroupSettings">
    <div
      v-show="editingOutputIndex === null || editingOutputIndex === undefined"
    >
      <paper
        :title="groupSettings.name"
        body-class="mt-0"
      >
        <template v-slot:header-right>
          <b-button
            variant="icon-light"
            @click="$emit('remove')"
          >
            <GjIcon name="Delete" />
            Remove output group
          </b-button>
        </template>
        <div class="px-2 pb-1">
          <b-row>
            <b-col>
              <validation-provider
                #default="{ errors }"
                name="Custom group name"
                class="w-100"
              >
                <input-field
                  name="file_group-custom_name"
                  label="Custom name"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                  :info="templateInfo.customName"
                  info-position="label"
                  @openInfo="openInfo"
                >
                  <b-form-input
                    id="file_group-custom_name"
                    v-model="groupSettings.customName"
                    placeholder="Enter custom group name here..."
                    :state="errors.length > 0 ? false : null"
                    @input="e => $emit('customNameChange', e)"
                  />
                </input-field>
              </validation-provider>
            </b-col>
          </b-row>
          <b-row>
            <b-col />
          </b-row>
        </div>

        <div
        v-if="groupSettings.type === groupSettings.outputGroupsTypes.HLS || groupSettings.type === groupSettings.outputGroupsTypes.AppleSingleStreamHLS || groupSettings.type === groupSettings.outputGroupsTypes.AppleLLHLS"
          class="px-2 pb-1 pt-2 border-default border-t"
        >
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <validation-provider
                #default="{ errors }"
                name="Segment control"
                class="w-100"
              >
                <input-field
                  name="file_group-segment_control"
                  label="Segment control"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                  :info="templateInfo.segmentControl"
                  info-position="label"
                  @openInfo="openInfo"
                >
                  <custom-select
                    id="file_group-segment_control"
                    v-model="groupSettings.segmentControl"
                    placeholder="Select segment control"
                    :options="getOptions(groupSettings.segmentControlOptions)"
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              sm="6"
            >
              <validation-provider
                #default="{ errors }"
                name="Segment length"
                class="w-100"
                rules="required|numeric"
              >
                <input-field
                  name="file_group-segment_length"
                  label="Segment length (sec) *"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                  :info="templateInfo.segmentLength"
                  info-position="label"
                  @openInfo="openInfo"
                >
                  <b-input-group>
                    <b-form-input
                      id="file_group-segment_length"
                      v-model="groupSettings.segmentLength"
                      type="text"
                      placeholder="Enter segment length here..."
                      :state="errors.length > 0 ? false : null"
                    />
                  </b-input-group>
                </input-field>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <validation-provider
                #default="{ errors }"
                name="Min segment length"
                class="w-100"
                rules="required|numeric"
              >
                <input-field
                  name="file_group-min_segment_length"
                  label="Min segment length (sec) *"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                  :info="templateInfo.minSegmentLength"
                  info-position="label"
                  @openInfo="openInfo"
                >
                  <b-form-input
                    id="file_group-min_segment_length"
                    v-model="groupSettings.minSegmentLength"
                    type="text"
                    placeholder="Enter min segment length here..."
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
              </validation-provider>
            </b-col>

            <b-col
              v-if="isLiveJob"
              cols="12"
              sm="6"
            >
              <validation-provider
                #default="{ errors }"
                name="HLS size length"
                class="w-100"
                rules="required|numeric"
              >
                <input-field
                  name="hls_size_length"
                  label="HLS size length*"
                  label-position="top"
                  :error="errors.length ? errors[0] : null"
                  :info="templateInfo.hlsSizeLength"
                  info-position="label"
                  @openInfo="openInfo"
                >
                  <b-form-input
                    id="hls_size_length"
                    v-model="groupSettings.hlsListLength"
                    type="number"
                    placeholder="Enter hls size here ..."
                    :state="errors.length > 0 ? false : null"
                  />
                </input-field>
              </validation-provider>
            </b-col>
          </b-row>
        </div>
      </paper>
      <paper
        v-if="groupSettings.type === groupSettings.outputGroupsTypes.HLS ||
          groupSettings.type === groupSettings.outputGroupsTypes.DASH"
        title="DRM"
        body-class="mt-0"
      >
        <drm-settings
          :drm-settings="groupSettings.drmList"
          :group-drm="groupSettings.groupDRM"
          :has-drm="groupSettings.hasDRM"
          @useDrm="useDrm"
        />
      </paper>
      <paper
        title="Outputs"
        body-class="mt-0"
      >
        <template v-slot:header-right>
          <b-button
            variant="outline-primary"
            class="btn-paper-header"
            @click="addOutput"
          >
            Add Output
          </b-button>
        </template>
        <outputs-table
          :outputs="groupSettings.outputs"
          :is-h-l-s="groupSettings.type === groupSettings.outputGroupsTypes.HLS"
          :is-dash="groupSettings.type === groupSettings.outputGroupsTypes.DASH"
          @removeOutput="removeOutput"
          @editOutput="editOutput"
        />
      </paper>
    </div>
    <div
      v-if="
        editingOutputIndex !== null && groupSettings.outputs[editingOutputIndex]
      "
    >
      <single-output-settings
        ref="singleOutputsettings"
        :output="groupSettings.outputs[editingOutputIndex]"
        :group-type="groupSettings.type"
        :group-name="groupSettings.name"
        :has-duplicate-name="
          hasDuplicateName(
            groupSettings.outputs[editingOutputIndex].nameModifier,
          )
        "
        :has-duplicate-folder-name="
          hasDuplicateFolderName(groupSettings.outputs[editingOutputIndex].name)
        "
        :is-h-l-s="groupSettings.type === groupSettings.outputGroupsTypes.HLS"
        :is-dash="groupSettings.type === groupSettings.outputGroupsTypes.DASH"
        :tab="activeTab"
        @close="closeSingleOutputSettings"
        @openInfo="openInfo"
        @remove="() => removeOutput(editingOutputIndex)"
      />
    </div>
  </div>
  <loading-page v-else />
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import Paper from '@/components/paper/Paper.vue'
import OutputsTable from './OutputsTable.vue'
import SingleOutputSettings from './SingleOutputSettings.vue'
import DrmSettings from './DrmSettings.vue'

import templateInfo from './templateInfo'

export default {
  name: 'JobOutputSettings',
  components: {
    Paper,
    ValidationProvider,
    OutputsTable,
    SingleOutputSettings,
    DrmSettings,
  },
  props: {
    tab: {
      type: Object,
      default: null,
    },
    active: {
      type: String,
      default: null,
    },
    edit: {
      type: Boolean,
      default: null,
    },
    editingOutputIndex: {
      type: Number,
      default: null,
    },
    isLiveJob: {
      type: Boolean,
      default: false,
    },
    activeTab: {
      type: Object,
      default: null,
    },
    jobTemplate: {
      type: Object,
      default: null,
    },
    fromLiveJobs: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      required,
      templateInfo,
      fetchingGroupSettings: false,
      groupSettings: null,
      outputModel: null,
      lastAddedOutputName: 1,
    }
  },
  computed: {
    editingOutput() {
      return this.editingOutputIndex >= 0
    },
  },

  created() {
    this.init()
  },
  methods: {
    init() {
      this.groupSettings = this.tab.outputGroup
      this.outputModel = this.edit
        ? { ...this.tab.outputGroup.emptyOutputModel }
        : { ...this.tab.outputGroup.outputs[0] }
      this.lastAddedOutputName = this.tab.outputGroup.outputs.length
    },
    removeOutput(index) {
      this.confirmDelete(
        'Remove output group',
        'Are you sure you want to remove this output group?',
        'Remove',
      ).then(ok => {
        if (ok) {
          this.groupSettings.outputs.splice(index, 1)
          this.closeSingleOutputSettings()
        }
      })
    },
    closeSingleOutputSettings() {
      this.$emit('editingOutputIndex', null)
    },
    addOutput() {
      const newOutput = {
        ...this.outputModel,
        name: `Output${this.lastAddedOutputName++ + 1}`,
      }
      this.groupSettings.outputs.push(newOutput)
    },
    editOutput(index) {
      this.$emit('editingOutputIndex', index)
    },
    hasDuplicateName(name) {
      const removeByName = this.groupSettings.outputs.filter(
        o => o.nameModifier !== name,
      )
      const dif = this.groupSettings.outputs.length - removeByName.length
      return dif > 1
    },
    hasDuplicateFolderName(name) {
      if (
        this.groupSettings.type !== this.groupSettings.outputGroupsTypes.HLS
        && this.groupSettings.type !== this.groupSettings.outputGroupsTypes.DASH
      ) return false
      const removeByName = this.groupSettings.outputs.filter(
        o => o.name !== name,
      )
      const dif = this.groupSettings.outputs.length - removeByName.length
      return dif > 1
    },
    openInfo(e) {
      this.$emit('openInfo', e)
    },
    useDrm(val) {
      this.groupSettings.hasDRM = val
    },
  },
}
</script>

<style></style>
